import './Explorer.css';

class Explorer {
  constructor(container, options = {}) {
    this.container = container;
    this.options = options;
    this.isVisible = true;
    this.fileTree = null;
    this.activeFilepath = null;
    
    // Store reference to this component on the container for external access
    container.__explorerComponent = this;
    
    // Bind methods
    this.handleFolderClick = this.handleFolderClick.bind(this);
    this.handleFileClick = this.handleFileClick.bind(this);
    this.setActiveFile = this.setActiveFile.bind(this);
    this.toggle = this.toggle.bind(this);
    
    // Initialize state based on screen size
    this.initializeState();
    
    // Add resize listener to handle responsive state
    window.addEventListener('resize', this.initializeState.bind(this));
  }
  
  /**
   * Initialize state based on screen size
   */
  initializeState() {
    // Check if we're on mobile
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const toggleButton = document.getElementById('nav-drawer-toggle');
    
    // On initial mobile load, explorer should be hidden
    if (isMobile) {
      this.isVisible = false;
      this.container.classList.remove('open');
      if (toggleButton) {
        toggleButton.classList.remove('active');
      }
    } else {
      // On desktop, explorer should be visible
      this.isVisible = true;
      this.container.style.display = 'flex';
      if (toggleButton) {
        toggleButton.classList.add('active');
      }
    }
  }
  
  /**
   * Render the explorer component
   */
  render() {
    // Add the container class
    this.container.className = 'explorer';
    
    this.container.innerHTML = `
      <div class="explorer__header">
        <h3>EXPLORER</h3>
      </div>
      <ul class="explorer__file-tree-root">
        <li class="explorer__folder expanded">
          <span class="explorer__folder-name"><span class="icon material-symbols-sharp">folder_open</span><span>our-appologies-main</span></span>
          <ul class="explorer__file-tree-children">
            <li class="explorer__folder expanded">
              <span class="explorer__folder-name"><span class="icon material-symbols-sharp">folder_open</span><span>about</span></span>
              <ul class="explorer__file-tree-children">
                <li class="explorer__file-item" data-filepath="about/the-biz.info"><span>📄 the-biz.info</span></li>
                <li class="explorer__file-item" data-filepath="about/the-team.md"><span>📄 the-team.md</span></li>
              </ul>
            </li>
            <li class="explorer__folder expanded">
              <span class="explorer__folder-name"><span class="icon material-symbols-sharp">folder_open</span><span>apps</span></span>
              <ul class="explorer__file-tree-children">
                <li class="explorer__file-item" data-filepath="apps/VIBED_CREATIONS.apps"><span>📄 VIBED_CREATIONS.apps</span></li>
                <li class="explorer__file-item" data-filepath="apps/components_4_$ale.jsx"><span>📄 components_4_$ale.jsx</span></li>
              </ul>
            </li>
            <li class="explorer__folder expanded">
              <span class="explorer__folder-name"><span class="icon material-symbols-sharp">folder_open</span><span>ologies</span></span>
              <ul class="explorer__file-tree-children">
                <li class="explorer__file-item" data-filepath="ologies/EVERYTHING.json"><span>📄 EVERYTHING.json</span></li>
              </ul>
            </li>
            <li class="explorer__folder expanded">
              <span class="explorer__folder-name"><span class="icon material-symbols-sharp">folder_open</span><span>were_not_sorry</span></span>
              <ul class="explorer__file-tree-children">
                <li class="explorer__file-item" data-filepath="were_not_sorry/README.sub"><span>📄 README.sub</span></li>
              </ul>
            </li>
            <li class="explorer__folder expanded">
              <span class="explorer__folder-name"><span class="icon material-symbols-sharp">folder_open</span><span>dm_us</span></span>
              <ul class="explorer__file-tree-children">
                <li class="explorer__file-item" data-filepath="dm_us/do-it.sh"><span>📄 do-it.sh</span></li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
    `;
    
    this.fileTree = this.container.querySelector('.explorer__file-tree-root');
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Re-apply initial state after rendering
    this.initializeState();
    
    return this;
  }
  
  /**
   * Set up event listeners for the explorer
   */
  setupEventListeners() {
    // Event delegation for folder clicks
    this.container.addEventListener('click', (e) => {
      const folderName = e.target.closest('.explorer__folder-name');
      if (folderName) {
        this.handleFolderClick(folderName);
      }
      
      const fileItem = e.target.closest('.explorer__file-item');
      if (fileItem) {
        this.handleFileClick(fileItem);
      }
    });
    
    // Listen for tab activation events to update active file in explorer
    document.addEventListener('tab:activate', (e) => {
      if (e.detail && e.detail.tabId) {
        this.setActiveFile(e.detail.tabId);
      }
    });
  }
  
  /**
   * Handle folder click to expand/collapse
   */
  handleFolderClick(folderElement) {
    const folder = folderElement.closest('.explorer__folder');
    const children = folder.querySelector('.explorer__file-tree-children');
    const icon = folderElement.querySelector('.icon');
    
    if (folder.classList.contains('expanded')) {
      folder.classList.remove('expanded');
      children.style.display = 'none';
      icon.textContent = 'folder';
    } else {
      folder.classList.add('expanded');
      children.style.display = 'block';
      icon.textContent = 'folder_open';
    }
  }
  
  /**
   * Handle file click to open in tab
   */
  handleFileClick(fileElement) {
    const filepath = fileElement.dataset.filepath;
    if (filepath) {
      // Update active file
      this.setActiveFile(filepath);
      
      // Emit custom event for regular file open
      const event = new CustomEvent('file:open', { 
        detail: { filepath },
        bubbles: true 
      });
      this.container.dispatchEvent(event);
    }
  }
  
  /**
   * Set the active file in the explorer
   * @param {string} filepath - The file path to set as active
   */
  setActiveFile(filepath) {
    // Remove active class from current active file
    const currentActive = this.container.querySelector('.explorer__file-item.active');
    if (currentActive) {
      currentActive.classList.remove('active');
    }
    
    // Set active class on new active file
    const newActive = this.container.querySelector(`.explorer__file-item[data-filepath="${filepath}"]`);
    if (newActive) {
      newActive.classList.add('active');
    }
    
    // Update active filepath
    this.activeFilepath = filepath;
  }
  
  /**
   * Toggle explorer visibility
   * @param {boolean|undefined} forcedState - Force a specific state (true=visible, false=hidden, undefined=toggle)
   */
  toggle(forcedState) {
    // Get toggle button for styling
    const toggleButton = document.getElementById('nav-drawer-toggle');
    
    // Detect if we're on mobile
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    
    // Set visibility based on forcedState or toggle current state
    if (forcedState !== undefined) {
      this.isVisible = forcedState;
    } else {
      this.isVisible = !this.isVisible;
    }
    
    // Immediately update toggle button state based on visibility
    // This must happen before any other DOM operations to avoid visual lag
    if (toggleButton) {
      // Force DOM reflow to ensure visual update happens immediately
      if (this.isVisible) {
        toggleButton.classList.remove('active');
        void toggleButton.offsetWidth; // Force reflow
        toggleButton.classList.add('active');
      } else {
        toggleButton.classList.remove('active');
      }
    }
    
    // Toggle overlay for mobile
    this.toggleMobileOverlay(this.isVisible && isMobile);
    
    if (isMobile) {
      // On mobile - use class for slide animation
      if (this.isVisible) {
        this.container.classList.add('open');
        // Ensure button shows active state
        if (toggleButton) toggleButton.classList.add('active');
      } else {
        this.container.classList.remove('open');
        // Ensure button shows inactive state
        if (toggleButton) toggleButton.classList.remove('active');
      }
    } else {
      // On desktop - use display property
      this.container.style.display = this.isVisible ? 'flex' : 'none';
    }
    
    // Emit custom event for toggle
    const event = new CustomEvent('explorer:toggle', { 
      detail: { isVisible: this.isVisible },
      bubbles: true 
    });
    this.container.dispatchEvent(event);
    
    return this.isVisible;
  }
  
  /**
   * Toggle mobile overlay
   * @param {boolean} show - Whether to show the overlay
   */
  toggleMobileOverlay(show) {
    let overlay = document.getElementById('mobile-nav-overlay');
    
    if (show) {
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'mobile-nav-overlay';
        overlay.classList.add('mobile-overlay');
        document.body.appendChild(overlay);
        
        // Add click handler to close drawer when clicking overlay
        overlay.addEventListener('click', () => {
          this.toggle(false);
        });
      }
      overlay.classList.add('active');
    } else if (overlay) {
      overlay.classList.remove('active');
    }
  }
}

export default Explorer; 